import {
  hashQueryKey,
  QueryClient,
  UseMutationOptions,
  useQueryClient,
} from 'react-query';
import {
  IndexedParticipation,
  Participation,
  ParticipationStatus,
} from './participation';
import { useCallback, useMemo } from 'react';
import { ParticipationUpdateData } from './participationQueries';
import { useGetIndexedAnswerText } from './utils/useIndexedAnswerText';
import { CriterionStatusEnum } from './application';
import { useParticipantFieldsByName } from './participantFields';
import { SelectionPayload } from './participations/selection';

export interface PaginatedResults<T> {
  data: T[];
  pagination: {
    count: number;
    cursor: string;
  };
}

export interface CacheStateSnapshot {
  listData: Array<[any, any]>;
  counts: any;
}

export const paidStatus = [
  ParticipationStatus.PAYMENT_REQUESTED,
  ParticipationStatus.PROCESSING_PAYMENT,
  ParticipationStatus.PAYED,
];

export const declinedStatus = [
  ParticipationStatus.DECLINED,
  ParticipationStatus.CANCELED,
  ParticipationStatus.NO_SHOW,
];

export const useGetParticipationFromCache = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (id: string): Participation | undefined => {
      return queryClient.getQueryData(['participations', id]);
    },
    [queryClient],
  );
};

const useSetQueryDataIfDifferent = (
  toIgnoreQueryKey?: any[],
  onlyIfEmpty?: boolean,
) => {
  const queryClient = useQueryClient();
  const hash = hashQueryKey(toIgnoreQueryKey || []);
  return useCallback(
    (queryKey: any[], data: any) => {
      if (hashQueryKey(queryKey) === hash) {
        return;
      }
      const current = queryClient.getQueryData(queryKey);
      if (onlyIfEmpty && current) {
        return;
      }
      if (current !== data) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    [queryClient, hash],
  );
};

const useReplaceParticipationIsListCache = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (
      queryKey: any,
      data: any,
      participation: Participation | IndexedParticipation,
    ): boolean => {
      if (!data || typeof data !== 'object') {
        return false;
      }

      const statusIndex = queryKey.find((k: string) => k === 'v2') ? 5 : 4;
      let shouldAddAgain =
        queryKey.length > 4 && // ['sessions', id, 'participations', type, status]
        Array.isArray(queryKey[statusIndex]) &&
        queryKey[statusIndex].includes(participation.status);

      if ('pages' in data && data.pages.length > 0) {
        let edited = false;
        let count = data.pages[data.pages.length - 1].pagination.count;
        const prevCount = count;

        for (const page of data.pages) {
          const index = page.data.findIndex(
            (p: IndexedParticipation) => p.id === participation.id,
          );
          if (index === -1) {
            continue;
          }

          const current: IndexedParticipation = page.data[index];
          if (shouldAddAgain) {
            if (current === participation) {
              return false;
            }
            shouldAddAgain = false;
            page.data[index] =
              'formResponses' in participation
                ? (participation as IndexedParticipation)
                : toIndexedParticipation(participation, current);
            edited = true;
            break;
          }

          page.data.splice(index, 1);
          edited = true;
          count--;
        }

        if (shouldAddAgain) {
          const indexed =
            'formResponses' in participation
              ? (participation as IndexedParticipation)
              : toIndexedParticipation(participation);

          data.pages?.[0]?.data?.unshift(indexed);
          count++;
          edited = true;
        }

        if (prevCount !== count) {
          data.pages.forEach((page: any) => {
            page.pagination.count = count;
          });
        }

        if (edited) {
          queryClient.setQueryData(queryKey, data);
          return true;
        }
      }
      return false;
    },
    [queryClient],
  );
};

export const useUpdateParticipationCache = (
  queryKey?: any[],
  isUpdateMutation?: boolean,
  singleOnlyIfEmpty?: boolean,
) => {
  const queryClient = useQueryClient();
  const setQueryData = useSetQueryDataIfDifferent(queryKey, singleOnlyIfEmpty);
  const replaceInListCache = useReplaceParticipationIsListCache();
  const hash = queryKey ? hashQueryKey(queryKey) : '';
  return useCallback(
    (
      participation: Participation,
      options?: {
        isUpdateMutation?: boolean;
      } & Record<string, unknown>,
    ) => {
      setTimeout(() => {
        setQueryData(['participations', participation.id], participation);
        setQueryData(
          ['participations', participation.trackingId],
          participation,
        );
        if (participation.participant) {
          setQueryData(
            ['participations', participation.id, 'participant'],
            participation.participant,
          );
        }

        const queries = queryClient.getQueriesData([
          'sessions',
          participation.sessionId,
          'participations',
        ]);

        const keys = [];
        for (const [key, data] of queries) {
          if (hashQueryKey(key) === hash) {
            continue;
          }
          const changed = replaceInListCache(key, data, participation);
          if (changed) {
            keys.push(key);
          }
        }

        let forceInvalidate = false;
        if (options && options['isUpdateMutation']) {
          forceInvalidate = true;
        }
        if (!isUpdateMutation && !forceInvalidate) {
          return;
        }

        keys.forEach((k) => queryClient.invalidateQueries(k));
        const recruitmentCriteriaQueries = queryClient.getQueriesData({
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === 'recruitments' &&
            q.queryKey[2] === 'criteria',
        });

        for (const [key] of recruitmentCriteriaQueries) {
          queryClient.invalidateQueries(key);
        }

        const quotaQueries = queryClient.getQueriesData([
          'sessions',
          participation.sessionId,
          'quotas',
        ]);
        for (const [key] of quotaQueries) {
          queryClient.invalidateQueries(key);
        }

        const countQueries = queryClient.getQueriesData([
          'sessions',
          participation.sessionId,
          'participation-status',
          'grouped-count',
        ]);
        for (const [key] of countQueries) {
          queryClient.invalidateQueries(key);
        }
      });
    },
    [
      setQueryData,
      queryClient,
      queryKey,
      hash,
      isUpdateMutation,
      replaceInListCache,
    ],
  );
};

export const getStatusListForColumn = (status?: ParticipationStatus) => {
  if (!status) {
    return [];
  }
  if (declinedStatus.includes(status)) {
    return declinedStatus;
  }
  if (paidStatus.includes(status)) {
    return paidStatus;
  }
  return [status];
};

export const useUpdateStatusQueryOptions = <TParams extends Participation>(
  update: (p: TParams) => Participation,
  options: UseMutationOptions<Participation, Error, TParams, TParams> = {},
): UseMutationOptions<Participation, Error, TParams, TParams> => {
  const updateParticipationCache = useUpdateParticipationCache();

  const onError = useCallback(
    (err: Error, params: TParams, context?: TParams) => {
      if (options.onError) {
        return options.onError(err, params, context);
      }

      updateParticipationCache(params, { isUpdateMutation: true });
    },
    [updateParticipationCache, options],
  );

  const onMutate = useCallback(
    (participation: TParams) => {
      if (options.onMutate) {
        options.onMutate(participation);
      } else {
        const updatedParticipation = update(participation);
        updateParticipationCache(updatedParticipation);
      }

      return participation;
    },
    [update, updateParticipationCache, options],
  );
  const onSuccess = useCallback(
    (res: Participation, variables: TParams, ctx?: TParams) => {
      if (options.onSuccess) {
        options.onSuccess(res, variables, ctx);
        return;
      }

      updateParticipationCache(res, { isUpdateMutation: true });
    },
    [updateParticipationCache, options],
  );

  return {
    ...options,
    onError,
    onMutate,
    onSuccess,
  };
};

export const useUpdateStatusTable = (
  status: ParticipationStatus,
  previousStatus?: ParticipationStatus,
) => {
  const queryClient = useQueryClient();

  const onSuccess = useCallback(
    async (newParticipation: Participation) => {
      const queries = queryClient
        .getQueriesData([
          'sessions',
          newParticipation.sessionId,
          'participations',
          'count',
        ])
        .concat(
          queryClient.getQueriesData([
            'sessions',
            newParticipation.sessionId,
            'participations',
            'page',
          ]),
        );
      for (const [key] of queries) {
        const statusList = key[4];
        if (
          Array.isArray(statusList) &&
          [status, previousStatus].some((s) => statusList.includes(s))
        ) {
          await queryClient.invalidateQueries(key);
        }
      }
    },
    [previousStatus, queryClient, status],
  );

  return {
    onSuccess,
  };
};

export const useParticipationStatusUpdate = (
  newStatus: ParticipationStatus,
) => {
  const queryClient = useQueryClient();
  const replaceInListCache = useReplaceParticipationIsListCache();

  const onMutate = useCallback(
    async (participation: Participation): Promise<ParticipationUpdateData> => {
      const oldStatus = participation.status;

      await queryClient.cancelQueries(
        getParticipationQueriesKey(participation.sessionId),
      );

      const snapshot = optimisticStatusUpdate(
        queryClient,
        participation,
        newStatus,
        replaceInListCache,
      );

      return {
        ...participation,
        oldStatus,
        snapshot,
      };
    },
    [queryClient, newStatus, replaceInListCache],
  );

  const onError = useCallback(
    (
      _err: Error,
      _variables: Participation,
      data?: ParticipationUpdateData | Participation,
    ) => {
      if (data && 'snapshot' in data) {
        for (const [key, datum] of data.snapshot.listData) {
          queryClient.setQueryData(key, datum);
        }

        if (data.snapshot.counts) {
          queryClient.setQueryData(
            getGroupedCountKey(data.sessionId),
            data.snapshot.counts,
          );
        }
      }
    },
    [queryClient],
  );

  const onSuccess = useCallback(
    async (newParticipation: Participation, _variables: Participation) => {
      const indexedParticipation = toIndexedParticipation(newParticipation);
      updateParticipationListCaches(
        queryClient,
        newParticipation.sessionId,
        indexedParticipation,
        replaceInListCache,
        true,
      );

      queryClient.setQueryData(
        ['participations', indexedParticipation.id],
        indexedParticipation,
      );

      if (indexedParticipation.trackingId) {
        queryClient.setQueryData(
          ['participations', indexedParticipation.trackingId],
          indexedParticipation,
        );
      }

      await queryClient.invalidateQueries(
        getGroupedCountKey(newParticipation.sessionId),
      );
    },
    [queryClient, replaceInListCache],
  );

  return useMemo(
    () => ({
      onMutate,
      onError,
      onSuccess,
    }),
    [onMutate, onError, onSuccess],
  );
};

export const useBatchParticipationStatusUpdate = (
  newStatus: ParticipationStatus,
  options: ReturnType<typeof useParticipationStatusUpdate>,
) => {
  const queryClient = useQueryClient();
  const onMutate = useCallback(
    async (payload: {
      selectedParticipations?: IndexedParticipation[];
      deselectedIds?: string[];
    }): Promise<ParticipationUpdateData[]> => {
      let participationsToUpdate: IndexedParticipation[] = [];

      if (payload.selectedParticipations) {
        participationsToUpdate = payload.selectedParticipations.filter(
          (p) => !payload.deselectedIds?.includes(p.id),
        );
      }

      const contexts: ParticipationUpdateData[] = [];
      for (const participation of participationsToUpdate) {
        const context = await options.onMutate?.({
          ...participation,
          status: newStatus,
        });
        if (context) {
          contexts.push(context);
        }
      }

      return contexts;
    },
    [options, newStatus],
  );

  const onError = useCallback(
    (
      err: Error,
      payload: SelectionPayload,
      contexts?: ParticipationUpdateData[],
    ) => {
      contexts?.forEach((context) => {
        options.onError?.(err, context, context);
      });
    },
    [options],
  );

  const onSuccess = useCallback(
    async (results: Participation[], payload: SelectionPayload) => {
      await queryClient.invalidateQueries(
        getGroupedCountKey(payload.sessionId),
      );
      for (const result of results) {
        await options.onSuccess?.(result, result);
      }
    },
    [options, queryClient],
  );

  return { onMutate, onError, onSuccess };
};

const getParticipationQueriesKey = (sessionId: string) => [
  'sessions',
  sessionId,
  'participations',
];

const getGroupedCountKey = (sessionId: string) => [
  'sessions',
  sessionId,
  'participation-status',
  'grouped-count',
];

const toIndexedParticipation = (
  participation: Participation,
  existingIndexed?: IndexedParticipation,
): IndexedParticipation => {
  return {
    ...participation,
    firstname:
      participation.participant?.firstName || existingIndexed?.firstname || '',
    lastname:
      participation.participant?.lastName || existingIndexed?.lastname || '',
    email: participation.participant?.email || existingIndexed?.email || '',
    meetingDate: participation.appointment?.date
      ? new Date(participation.appointment?.date)
      : existingIndexed?.meetingDate,
    formResponses: existingIndexed?.formResponses || [],
    criteriaStatus: existingIndexed?.criteriaStatus || {},
  };
};

const storeCacheState = (
  queryClient: QueryClient,
  sessionId: string,
): CacheStateSnapshot => {
  const queries = queryClient.getQueriesData(
    getParticipationQueriesKey(sessionId),
  );

  const listData: Array<[any, any]> = [];
  for (const [key] of queries) {
    if (key.includes('count') || key.includes('grouped-count')) {
      continue;
    }

    const currentData = queryClient.getQueryData(key);
    const clonedData = currentData ? structuredClone(currentData) : currentData;
    listData.push([key, clonedData]);
  }

  const counts = queryClient.getQueryData(getGroupedCountKey(sessionId));

  return { listData, counts };
};

export const updateParticipationListCaches = (
  queryClient: QueryClient,
  sessionId: string,
  participation: IndexedParticipation,
  replaceInListCache: (
    key: any,
    data: any,
    item: IndexedParticipation,
  ) => boolean,
  onlyIfChanged = false,
): void => {
  const queries = queryClient.getQueriesData(
    getParticipationQueriesKey(sessionId),
  );

  for (const [key, data] of queries) {
    if (key.includes('count') || key.includes('grouped-count')) {
      continue;
    }

    const clonedData = data ? structuredClone(data) : data;
    const changed = replaceInListCache(key, clonedData, participation);

    if (!onlyIfChanged || changed) {
      queryClient.setQueryData(key, clonedData);
    }
  }
};

const updateGroupedCountCache = (
  queryClient: QueryClient,
  sessionId: string,
  oldStatus: ParticipationStatus,
  newStatus: ParticipationStatus,
): void => {
  const countQuery = queryClient.getQueryData(getGroupedCountKey(sessionId));

  if (countQuery && typeof countQuery === 'object') {
    const updated = { ...(countQuery as Record<string, number>) };

    if (updated[oldStatus] !== undefined) {
      updated[oldStatus] = Math.max(0, updated[oldStatus] - 1);
    }

    updated[newStatus] = (updated[newStatus] || 0) + 1;

    queryClient.setQueryData(getGroupedCountKey(sessionId), updated);
  }
};

const optimisticStatusUpdate = (
  queryClient: QueryClient,
  participation: Participation,
  newStatus: ParticipationStatus,
  replaceInListCache: (
    key: any,
    data: any,
    item: IndexedParticipation,
  ) => boolean,
): CacheStateSnapshot => {
  const oldStatus = participation.status;
  const updatedParticipation = toIndexedParticipation({
    ...participation,
    status: newStatus,
  });
  const snapshot = storeCacheState(queryClient, participation.sessionId);

  updateParticipationListCaches(
    queryClient,
    participation.sessionId,
    updatedParticipation,
    replaceInListCache,
  );

  updateGroupedCountCache(
    queryClient,
    participation.sessionId,
    oldStatus,
    newStatus,
  );

  return snapshot;
};

export const useUpdateIndexedParticipationCache = () => {
  const queryClient = useQueryClient();
  const setQueryData = useSetQueryDataIfDifferent(undefined, true);
  const participantFieldsByName = useParticipantFieldsByName();
  const getIndexedAnswerText = useGetIndexedAnswerText();
  return useCallback(
    (p: IndexedParticipation) => {
      setTimeout(() => {
        const participant = {
          id: p.participantId,
          firstName: p.firstname,
          lastName: p.lastname,
          email: p.email,
          fullName: p.fullname,
        };

        setQueryData(['participations', p.id], {
          ...p,
          mainApplicationId: p.applicationId,
          participant,
        });
        setQueryData(['participations', p.trackingId], p);
        setQueryData(['participations', p.id, 'participant'], participant);

        setQueryData(
          ['applications', p.applicationId, 'answers-texts'],
          p.formResponses?.map((r) => {
            const field = participantFieldsByName[r.fieldRef];
            return {
              fieldRef: r.fieldRef,
              question: field?.range?.label || field?.label || undefined,
              answer: getIndexedAnswerText(r),
              status:
                p.criteriaStatus?.[r.fieldRef] || CriterionStatusEnum.IGNORE,
            };
          }),
        );
      });
    },
    [setQueryData, queryClient, getIndexedAnswerText, participantFieldsByName],
  );
};
